import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLanguages } from '@/hooks/useLanguages';
import { useTranslations } from '@/hooks/useTranslations';

interface TranslationContextType {
  currentLanguage: string;
  setCurrentLanguage: (language: string) => void;
  languages: any[];
  translateText: (text: string) => Promise<string>;
  loading: boolean;
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

export const TranslationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { languages, getDefaultLanguage } = useLanguages();
  const { translateText: translateWithAPI, settings, loading: translationsLoading } = useTranslations();
  
  const [currentLanguage, setCurrentLanguage] = useState<string>(() => {
    // Start with stored preference or English as a safe default
    return localStorage.getItem('selected-language') || 'en';
  });

  useEffect(() => {
    // Always align with admin-configured default when available
    if (settings?.default_language) {
      setCurrentLanguage(settings.default_language);
    } else if (!localStorage.getItem('selected-language')) {
      // If no settings and no stored preference, fallback to English
      setCurrentLanguage('en');
    }
  }, [settings]);

  useEffect(() => {
    // Get default language from languages table as final fallback
    const defaultLang = getDefaultLanguage();
    if (!settings?.default_language && !localStorage.getItem('selected-language')) {
      setCurrentLanguage(defaultLang?.code || 'en');
    }
  }, [languages, getDefaultLanguage, settings]);

  useEffect(() => {
    localStorage.setItem('selected-language', currentLanguage);
  }, [currentLanguage]);

  const translateText = async (text: string): Promise<string> => {
    const baseLang = settings?.default_language || 'en';
    if (!text || currentLanguage === baseLang) return text; // Don't translate default language
    try {
      return await translateWithAPI(text, baseLang, currentLanguage);
    } catch (error) {
      console.error('Translation error:', error);
      return text;
    }
  };

  return (
    <TranslationContext.Provider
      value={{
        currentLanguage,
        setCurrentLanguage,
        languages,
        translateText,
        loading: translationsLoading,
      }}
    >
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};